import React, { useState } from "react";
import moment from "moment";
import classNames from "classnames";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import useListLayout, { cardGridClass } from "../../hooks/useListLayout";
import { getHeartbeatStatus } from "../../utils";
import { useHeartbeatThresholds } from "../../hooks/useSettings";
import "../../starek.css";

const DEVICE_IMAGE_BASE = "https://00117-starek.s3.us-east-1.amazonaws.com/branding";

/** The model's product shot, or the microchip icon when there is none. */
function DeviceImage({ model }) {
    const [failed, setFailed] = useState(false);
    return (
        <div className="roundel roundel-md me-3">
            {model && !failed ? (
                <img
                    src={`${DEVICE_IMAGE_BASE}/${model}.png`}
                    alt={model}
                    style={{ objectFit: "contain" }}
                    onError={() => setFailed(true)}
                />
            ) : (
                <i className="fa fa-microchip fa-lg text-muted"></i>
            )}
        </div>
    );
}

/**
 * Devices as cards, in the same header-bar style as the Parts and Operations card
 * views. The signal icons, maintenance status and actions menu are the list view's
 * own cells (passed in as render functions), so a card behaves exactly like its row.
 */
function DevicesCardView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    onOpen,
    renderSignals,
    renderMaintenance,
    renderActions,
}) {
    const { columns } = useListLayout();
    const heartbeatThresholds = useHeartbeatThresholds();

    const isSelected = (id) => selectedRows.some((row) => row.id === id);

    const toggleSelected = (device) => {
        const next = isSelected(device.id)
            ? selectedRows.filter((row) => row.id !== device.id)
            : [...selectedRows, device];
        onSelectedRowsChange?.({ selectedRows: next });
    };

    // `items` is already the current page, searched on the server. The page shows
    // its own empty state when the plant has no devices at all.
    if (items.length === 0) {
        return (
            <EntityEmptyState
                icon="fa-microchip"
                entityName="Device"
                messages={EMPTY_MESSAGES.devices}
                isFiltered={!!filterText?.trim()}
            />
        );
    }

    const stop = (e) => e.stopPropagation();

    return (
        <div className={cardGridClass(columns)}>
            {items.map((device) => {
                const name = device.name || device.id;
                const offline = device.device?.controlledOffline;
                const lastSeen = device.device?.lastSeen?.master;
                const heartbeat = getHeartbeatStatus(lastSeen, heartbeatThresholds);
                const cycles = (device.jobSchedules || []).reduce(
                    (sum, js) => sum + (js.completed_cycles || 0),
                    0,
                );
                const actions = renderActions?.(device);

                return (
                    <div key={device.id} className="col">
                        <div
                            className={`card h-100 shadow-sm ${
                                isSelected(device.id) ? "border-primary border-2" : ""
                            }`}
                            style={{ cursor: "pointer", opacity: device.is_active === false ? 0.55 : 1 }}
                            onClick={() => onOpen?.(device)}
                        >
                            <div className="card-header bg-primary text-white">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                        <div className="form-check mb-0 me-2" onClick={stop}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isSelected(device.id)}
                                                onChange={() => toggleSelected(device)}
                                                onClick={stop}
                                                aria-label={`Select ${name}`}
                                            />
                                        </div>
                                        <h5 className="card-title mb-0 text-truncate" title={name}>
                                            {name}
                                        </h5>
                                    </div>
                                    {actions && (
                                        // White chip so the list's grey menu icon reads on the blue bar.
                                        <div className="bg-white rounded ms-2" onClick={stop}>
                                            {actions}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="card-body d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    <DeviceImage model={device.device?.model} />
                                    <div className="overflow-hidden">
                                        <div className="text-truncate" style={{ fontSize: "0.875rem" }}>
                                            {device.device?.model ? `[${device.device.model}]` : "Model"}
                                            {device.is_active === false && (
                                                <span className="badge bg-secondary ms-2">Inactive</span>
                                            )}
                                        </div>
                                        <small
                                            className="text-muted d-block text-truncate"
                                            title={`FW ${device.device?.firmware_version || "—"}`}
                                        >
                                            FW {device.device?.firmware_version || "—"} · {cycles} Cycles
                                        </small>
                                    </div>
                                </div>

                                {!offline && (
                                    <div className="d-flex mb-3" onClick={stop}>
                                        {renderSignals?.(device)}
                                    </div>
                                )}

                                <div className="border-top pt-2 mt-auto">
                                    {/* Connection on the left, maintenance on the right. */}
                                    <div className="d-flex align-items-start justify-content-between gap-2">
                                        {offline ? (
                                            <small className="text-muted">Waiting to reconnect</small>
                                        ) : (
                                            <span
                                                className={classNames(
                                                    "tw-px-2 tw-py-1 tw-rounded tw-text-xs text-nowrap",
                                                    heartbeat.style,
                                                )}
                                            >
                                                {heartbeat.label}
                                            </span>
                                        )}
                                        <div className="text-end overflow-hidden">
                                            {renderMaintenance?.(device)}
                                        </div>
                                    </div>
                                    {!offline && (
                                        <small className="text-muted d-block text-nowrap mt-2">
                                            <i className="fas fa-clock me-1"></i>
                                            {lastSeen ? moment(lastSeen).format("MMM DD, YYYY hh:mm A") : "Never"}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default DevicesCardView;
